import { useCallback, useMemo, useRef, type RefObject } from 'react';
import type { LayoutChangeEvent, ScrollView } from 'react-native';

export type UseScrollToCenterResult = {
  scrollRef: RefObject<ScrollView | null>;
  onLayout: (e: LayoutChangeEvent) => void;
  onContentSizeChange: (contentWidth: number) => void;
  reset: () => void;
  /** Scroll to horizontal center (e.g. after zoom returns to 1×). */
  scrollToCenter: (animated?: boolean) => void;
};

/**
 * Centers a horizontal ScrollView once content and layout widths are known.
 * Call `reset` when the ScrollView remounts (layout change, mode toggle).
 */
export function useScrollToCenter(): UseScrollToCenterResult {
  const scrollRef = useRef<ScrollView>(null);
  const layoutWidthRef = useRef(0);
  const contentWidthRef = useRef(0);
  const hasScrolledRef = useRef(false);

  const reset = useCallback(() => {
    hasScrolledRef.current = false;
  }, []);

  const tryScrollToCenter = useCallback(() => {
    if (hasScrolledRef.current) return;
    const contentWidth = contentWidthRef.current;
    const layoutWidth = layoutWidthRef.current;
    if (contentWidth <= 0 || layoutWidth <= 0) return;
    const maxOffset = Math.max(0, contentWidth - layoutWidth);
    hasScrolledRef.current = true;
    scrollRef.current?.scrollTo({ x: maxOffset / 2, animated: false });
  }, []);

  const scrollToCenter = useCallback((animated = false) => {
    const contentWidth = contentWidthRef.current;
    const layoutWidth = layoutWidthRef.current;
    if (contentWidth <= 0 || layoutWidth <= 0) return;
    const maxOffset = Math.max(0, contentWidth - layoutWidth);
    hasScrolledRef.current = true;
    scrollRef.current?.scrollTo({ x: maxOffset / 2, animated });
  }, []);

  const onLayout = useCallback(
    (e: LayoutChangeEvent) => {
      layoutWidthRef.current = e.nativeEvent.layout.width;
      tryScrollToCenter();
    },
    [tryScrollToCenter]
  );

  const onContentSizeChange = useCallback(
    (contentWidth: number) => {
      const prevWidth = contentWidthRef.current;
      contentWidthRef.current = contentWidth;
      if (prevWidth > 0 && Math.abs(prevWidth - contentWidth) > 1) {
        hasScrolledRef.current = false;
      }
      tryScrollToCenter();
    },
    [tryScrollToCenter]
  );

  return useMemo(
    () => ({ scrollRef, onLayout, onContentSizeChange, reset, scrollToCenter }),
    [onLayout, onContentSizeChange, reset, scrollToCenter]
  );
}
